import React, { useRef, useState, useEffect } from "react";
import { string, bool, array } from "prop-types";
import { ResponsiveImage } from "@quintype/components";
import { HighlightTitle } from "../../atoms/HighlightTitle";
import { HighlightText } from "../../atoms/HighlightText";
import "./highlight-card.m.css";

const imgAtomHighlightImage = "https://www.figma.com/api/mcp/asset/3754641d-9b90-4d0d-be09-e48c1ee6bb6b";

export const HighlightCard = ({ imageUrl, storyElements = [], galleryImages = [], showCounter = false }) => {
  const sliderRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalImages = galleryImages.length;

  // Handle scroll to update current index
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || totalImages <= 1) return;

    const handleScroll = () => {
      const scrollLeft = slider.scrollLeft;
      const slideWidth = slider.offsetWidth;
      const newIndex = Math.round(scrollLeft / slideWidth);
      setCurrentIndex(newIndex);
    };

    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, [totalImages]);

  const scrollToSlide = (index) => {
    const slider = sliderRef.current;
    if (!slider) return;
    const slideWidth = slider.offsetWidth;
    slider.scrollTo({ left: index * slideWidth, behavior: "smooth" });
  };

  const handlePrev = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : totalImages - 1;
    scrollToSlide(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < totalImages - 1 ? currentIndex + 1 : 0;
    scrollToSlide(newIndex);
  };

  // Render gallery image
  const renderGalleryImage = (image, index) => {
    const imageS3Key = image?.["image-s3-key"];
    const imageMetadata = image?.["image-metadata"];
    
    return (
      <div key={index} styleName="slider-slide">
        {imageS3Key ? (
          <ResponsiveImage
            slug={imageS3Key}
            metadata={imageMetadata}
            aspectRatio={[16, 9]}
            defaultWidth={600}
            widths={[400, 600, 800]}
            imgParams={{ auto: ["format", "compress"] }}
            alt={image?.title || "Gallery image"}
          />
        ) : (
          <img alt={image?.title || ""} src={imageUrl || imgAtomHighlightImage} />
        )}
      </div>
    );
  };

  // If storyElements are provided, render them dynamically
  if (storyElements && storyElements.length > 0) {
    // Separate text and image elements
    const textElements = storyElements.filter((el) => el?.type === "text");
    const imageElement = storyElements.find((el) => el?.type === "image");
    const imageS3Key = imageElement?.["image-s3-key"];
    const imageMetadata = imageElement?.["image-metadata"];

    // Check if we have gallery images
    const hasGallery = galleryImages.length > 0;

    return (
      <div styleName="highlight-card">
        {/* Group all text elements together */}
        {textElements.length > 0 && (
          <div styleName="highlight-card-text">
            {textElements[0] && (
              <div styleName="highlight-text-content-0" dangerouslySetInnerHTML={{ __html: textElements[0]?.text || "" }} />
            )}
            {textElements[1] && (
              <div styleName="highlight-text-content-1" dangerouslySetInnerHTML={{ __html: textElements[1]?.text || "" }} />
            )}
          </div>
        )}
        {/* Render gallery slider or single image */}
        {hasGallery ? (
          <div styleName="highlight-image">
            {totalImages === 1 ? (
              // Single image - no slider
              renderGalleryImage(galleryImages[0], 0)
            ) : (
              // Multiple images - slider with scroll snap
              <div styleName="slider-container" ref={sliderRef}>
                {galleryImages.map((image, index) => renderGalleryImage(image, index))}
              </div>
            )}
            {/* Counter with arrows for multiple images */}
            {totalImages > 1 && (
              <div styleName="counter">
                <button styleName="counter-arrow arrow-prev" onClick={handlePrev} aria-label="Previous slide" />
                <span styleName="counter-text">{currentIndex + 1}/{totalImages}</span>
                <button styleName="counter-arrow arrow-next" onClick={handleNext} aria-label="Next slide" />
              </div>
            )}
          </div>
        ) : imageElement ? (
          <div styleName="highlight-image">
            {imageS3Key ? (
              <ResponsiveImage
                slug={imageS3Key}
                metadata={imageMetadata}
                aspectRatio={[4, 3]}
                defaultWidth={300}
                widths={[200, 300, 400]}
                imgParams={{ auto: ["format", "compress"] }}
                alt={imageElement?.title || "Highlight"}
              />
            ) : (
              <img alt={imageElement?.title || ""} src={imageUrl || imgAtomHighlightImage} />
            )}
          </div>
        ) : null}
      </div>
    );
  }
};

HighlightCard.propTypes = {
  title: string,
  text: string,
  imageUrl: string,
  storyElements: array,
  galleryImages: array,
  showCounter: bool,
};

